// ===================================================================
// PrescriptionManager.tsx - Gestión de Recetas Electrónicas
// ===================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileSignature,
  Plus,
  Search,
  XCircle,
  CheckCircle,
  Clock,
  ShieldCheck,
  Settings,
  Share2,
  ArrowLeft,
  AlertTriangle,
  IdCard,
  QrCode,
  Download,
  Calendar,
  User,
  Pill
} from 'lucide-react';
import SignaturePad from './SignaturePad';
import PrescriptionWizard, { PacienteInicial } from './PrescriptionWizard';
import PrescriptionSettingsModal from './PrescriptionSettingsModal';

// Interfaces
interface Prescription {
  id: number;
  cuir: string;
  patient_name: string;
  patient_dni: string;
  diagnosis_description: string;
  prescriptions: PrescriptionItem[];
  status: 'active' | 'dispensed' | 'expired' | 'cancelled';
  issued_at: string;
  expires_at: string;
  dispensed_at?: string;
  // Lo que devuelve Qbi2 al registrar la receta afuera. Sin `qbi2_s3_link`
  // la receta existe solo acá y no hay PDF oficial que abrir.
  qbi2_estado?: string | null;
  qbi2_id_receta?: string | null;
  qbi2_cuir?: string | null;
  qbi2_s3_link?: string | null;
}

interface PrescriptionItem {
  medication: string;
  presentation: string;
  quantity: number;
  dosage: string;
  duration: string;
  notes?: string;
}

/** Lo poco que esta pantalla necesita saber de /prescriptions/eligibility. */
interface Habilitacion {
  puedeEmitir: boolean;
  titulo: string;
  detalle: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const PrescriptionManager: React.FC = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Estados
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewPrescription, setShowNewPrescription] = useState(false);
  const [showAjustes, setShowAjustes] = useState(false);
  const [aviso, setAviso] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState<boolean | null>(null);
  // La sugerencia de 2FA se puede descartar y no vuelve. Es un consejo, no una
  // tarea pendiente: no tiene por qué insistir cada vez que entra a Recetas.
  const [sugerenciaOculta, setSugerenciaOculta] = useState(
    () => localStorage.getItem('consultar:sugerencia2fa_oculta') === '1'
  );

  // Habilitación para emitir. `null` = todavía no sabemos.
  //
  // POR QUÉ SE VUELVE A PREGUNTAR ACÁ
  // ---------------------------------
  // El modal del panel también consulta esto, pero es la puerta y esta es la
  // habitación: a /professional/prescriptions se llega también con el botón
  // atrás del navegador o escribiendo la URL, sin pasar por el modal. Si el
  // profesional entró, fue a Ajustes y borró su matrícula, al volver seguía
  // viendo "Nueva receta" como si nada y recién se enteraba al final, con un
  // error del servidor después de haber cargado todos los medicamentos.
  //
  // El backend siempre rechazó la emisión sin matrícula (LICENSE_REQUIRED),
  // así que nunca salió una receta sin habilitación. Esto no arregla un
  // agujero de seguridad: le evita al médico perder el trabajo cargado.
  const [habilitacion, setHabilitacion] = useState<Habilitacion | null>(null);
  // Paciente traido desde un turno, para prellenar el asistente.
  const [pacienteDesdeTurno, setPacienteDesdeTurno] = useState<PacienteInicial | null>(null);

  // Cargar recetas
  useEffect(() => {
    loadPrescriptions();
    check2FAStatus();
  }, [pagination.page, statusFilter]);

  // La habilitación se consulta una vez al entrar, no en cada paginado.
  useEffect(() => {
    verificarHabilitacion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Llegada desde un turno del calendario: ?nuevaReceta=1&nombre=...&dni=...
  //
  // Los datos viajan por la URL, igual que "Crear HC con estos datos", para no
  // tener que retipear en la consulta a alguien que ya está en pantalla.
  // Después se limpian de la barra de direcciones: si el profesional recarga
  // la página no tiene por qué reabrirse el asistente, y menos con el DNI de
  // un paciente a la vista en el historial del navegador.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('nuevaReceta') !== '1') return;

    const leer = (k: string) => (q.get(k) || '').trim() || undefined;
    const sexoCrudo = (leer('sexo') || '').toUpperCase();
    setPacienteDesdeTurno({
      name: leer('nombre'),
      dni: leer('dni'),
      sex: (['F', 'M', 'X'].includes(sexoCrudo) ? sexoCrudo : '') as '' | 'F' | 'M' | 'X',
      birthDate: leer('nacimiento'),
    });
    setShowNewPrescription(true);
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  // Cierra el asistente si la respuesta llega DESPUÉS de que ya entró.
  //
  // Entre que se monta la pantalla y que contesta el servidor hay unas
  // décimas en las que todavía no sabemos nada: ahí el botón "Nueva receta"
  // está a la vista y un clic rápido abre el asistente igual. Se podría
  // deshabilitar el botón mientras carga, pero eso le pone un parpadeo gris
  // a las mil veces que sí está habilitado para cubrir la vez que no.
  // Es mejor dejar entrar y sacarlo apenas se sabe.
  useEffect(() => {
    if (showNewPrescription && habilitacion && !habilitacion.puedeEmitir) {
      setShowNewPrescription(false);
    }
  }, [habilitacion, showNewPrescription]);

  const verificarHabilitacion = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/prescriptions/eligibility`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (res.ok && json.success !== false) {
        setHabilitacion({
          puedeEmitir: Boolean(json.puedeEmitir),
          titulo: json?.mensaje?.titulo || 'No podés emitir recetas todavía',
          detalle: json?.mensaje?.detalle || 'Revisá los datos de tu matrícula.',
        });
      }
      // Si la consulta falla no se bloquea nada: el backend valida igual al
      // emitir, y dejar al profesional encerrado por un error de red sería
      // peor que dejarlo intentar.
    } catch {
      /* se ignora a propósito, ver arriba */
    }
  };

  const check2FAStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/2fa/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTwoFactorEnabled(data.enabled);
      }
    } catch (err) {
      console.error('Error verificando 2FA:', err);
    }
  };

  const loadPrescriptions = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/prescriptions/my-prescriptions?page=${pagination.page}&limit=${pagination.limit}`;
      
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Error cargando recetas');
      }

      const data = await response.json();
      setPrescriptions(data.prescriptions || []);
      setPagination(data.pagination || pagination);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    // Colores del sistema de iOS: verde #34C759, azul #007AFF, naranja #FF9500,
    // rojo #FF3B30. Se usan al 12% de opacidad de fondo, como las etiquetas
    // nativas.
    const badges = {
      active:    { color: 'bg-[#34C759]/12 text-[#248A3D]', icon: CheckCircle, text: 'Activa' },
      dispensed: { color: 'bg-[#007AFF]/12 text-[#0060DF]', icon: CheckCircle, text: 'Dispensada' },
      expired:   { color: 'bg-[#FF9500]/15 text-[#B25F00]', icon: Clock,       text: 'Expirada' },
      cancelled: { color: 'bg-[#FF3B30]/12 text-[#C82016]', icon: XCircle,     text: 'Cancelada' }
    };
    const badge = badges[status as keyof typeof badges] || badges.active;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[13px] font-semibold ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // El 2FA ya no frena la emisión. Antes esto tiraba un alert() y mandaba al
  // editor de perfil: el profesional salía de Recetas sin saber bien a qué,
  // y no volvía. Ahora se sugiere abajo y se puede seguir de largo.
  const handleNewPrescription = () => {
    // Segunda llave, por si en el futuro alguien abre el asistente desde otro
    // lado (un atajo, un turno, una notificación) sin pasar por el botón.
    if (habilitacion && !habilitacion.puedeEmitir) {
      navigate('/professional/habilitar-recetas');
      return;
    }
    setShowNewPrescription(true);
  };

  /**
   * Abrir una receta.
   *
   * Antes navegaba a /professional/prescriptions/:cuir, una ruta que nunca
   * existió en App.tsx: daba un 404. Y lo que el profesional quiere ver es el
   * PDF oficial de Qbi2, que además es lo que le manda al paciente.
   */
  const abrirReceta = (p: Prescription) => {
    if (p.qbi2_s3_link) {
      window.open(p.qbi2_s3_link, '_blank', 'noopener,noreferrer');
      return;
    }
    setAviso(
      p.qbi2_estado === 'rechazada' || p.status === 'cancelled'
        ? 'Esta receta no llegó a registrarse, así que no tiene PDF oficial.'
        : 'Esta receta se emitió antes de la integración y no tiene PDF oficial.'
    );
  };

  const compartirReceta = async (p: Prescription) => {
    if (!p.qbi2_s3_link) return;
    const numero = p.qbi2_cuir || p.qbi2_id_receta || p.cuir;
    // En celular abre el menú del sistema; en escritorio no existe y se copia.
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: 'Receta electrónica',
          text: `Receta electrónica N° ${numero}`,
          url: p.qbi2_s3_link,
        });
      } catch { /* lo canceló, no es un error */ }
      return;
    }
    navigator.clipboard?.writeText(p.qbi2_s3_link)
      .then(() => setAviso('Link copiado.'))
      .catch(() => setAviso('No pudimos copiar el link.'));
  };

  // Filtrar por búsqueda local
  const filteredPrescriptions = prescriptions.filter(p => 
    p.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.cuir.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.patient_dni && p.patient_dni.includes(searchTerm))
  );

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-10">
      {/* text-left a proposito. La app entera hereda text-align:center de
          la regla .App que viene en la plantilla de Create React App, asi
          que sin esto se centran los nombres, los CUIR y las fechas. */}
      <div className="max-w-3xl mx-auto px-4 text-left">
        {/* ---------------------------------------------------------------
            Cabecera al estilo iOS: volver discreto arriba, título grande
            debajo, y la acción principal separada del título.
            --------------------------------------------------------------- */}
        <div className="pt-5 pb-4">
          <button
            onClick={() => navigate('/dashboard/profesional')}
            className="flex items-center gap-1 text-[#0B7C87] text-[15px] mb-4 -ml-1 active:opacity-60 transition-opacity"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>

          {/* Título a la izquierda y el engranaje a la derecha, alineado con
              la primera línea del título como en los ajustes de iOS. */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-[34px] leading-tight font-bold text-gray-900 tracking-tight">
                Recetas
              </h1>
              <p className="text-[15px] text-gray-700 mt-0.5">
                Emitidas y certificadas electrónicamente
              </p>
            </div>

            <button
              onClick={() => setShowAjustes(true)}
              aria-label="Ajustes de recetas"
              title="Ajustes de recetas"
              className="mt-1.5 flex-shrink-0 w-11 h-11 rounded-full bg-white border border-gray-200 flex items-center justify-center active:scale-95 active:bg-gray-50 transition-all"
            >
              <Settings className="w-5 h-5 text-[#0B7C87]" />
            </button>
          </div>

          {/* Sin matrícula válida no se ofrece emitir, pero la lista de abajo
              queda visible: las recetas ya emitidas son documentos legales y
              el profesional tiene que poder consultarlas igual. Lo que se
              bloquea es emitir una nueva, no leer las viejas. */}
          {habilitacion && !habilitacion.puedeEmitir ? (
            <div className="mt-4 p-4 bg-[#FF9500]/10 border border-[#FF9500]/40 rounded-2xl">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#FF9500]/20 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-[#B35C00]" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 text-[15px]">
                    {habilitacion.titulo}
                  </h3>
                  <p className="text-gray-700 text-[13px] mt-0.5 leading-snug">
                    {habilitacion.detalle}
                  </p>
                  <button
                    onClick={() => navigate('/professional/habilitar-recetas')}
                    className="mt-3 inline-flex items-center gap-2 bg-[#0FA3B1] text-white px-4 py-2.5 rounded-xl font-semibold text-[14px] active:scale-[0.98] active:bg-[#0D8F9C] transition-all"
                  >
                    <IdCard className="w-4 h-4" />
                    Cargar mi matrícula
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={handleNewPrescription}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-[#0FA3B1] text-white px-5 py-3.5 rounded-2xl font-semibold text-[16px] shadow-sm active:scale-[0.98] active:bg-[#0D8F9C] transition-all"
            >
              <Plus className="w-5 h-5" />
              Nueva receta
            </button>
          )}
        </div>

        {/* Sugerencia de refuerzo de seguridad.
            Ojo con el tono: esto NO bloquea nada, las recetas salen igual sin
            2FA. Por eso no va en naranja de advertencia ni dice "falta". */}
        {twoFactorEnabled === false && !sugerenciaOculta && (
          <div className="mb-4 p-4 bg-white border border-[#0FA3B1]/30 rounded-2xl">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#0FA3B1]/10 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-[#0B7C87]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 text-[15px]">
                  Reforzá la seguridad de tu cuenta
                </h3>
                <p className="text-gray-700 text-[13px] mt-0.5 leading-snug">
                  Si querés, además de tu contraseña podés pedir un código al iniciar
                  sesión. Es opcional: las recetas funcionan igual sin esto.
                </p>
                <div className="flex items-center gap-5 mt-2.5">
                  <button
                    onClick={() => navigate('/professional/profile-config?tab=security')}
                    className="text-[#0B7C87] font-semibold text-[13px] active:opacity-60"
                  >
                    Ver cómo se activa
                  </button>
                  <button
                    onClick={() => {
                      setSugerenciaOculta(true);
                      localStorage.setItem('consultar:sugerencia2fa_oculta', '1');
                    }}
                    className="text-gray-600 font-medium text-[13px] active:opacity-60"
                  >
                    No, gracias
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {aviso && (
          <div className="mb-3 flex items-start gap-2 bg-[#0FA3B1]/10 border border-[#0FA3B1]/30 rounded-2xl px-4 py-3">
            <p className="text-[13px] text-gray-800 flex-1">{aviso}</p>
            <button
              onClick={() => setAviso('')}
              className="text-[13px] font-semibold text-[#0B7C87] flex-shrink-0"
            >
              Cerrar
            </button>
          </div>
        )}

        {/* Buscador estilo iOS: píldora gris, sin borde */}
        <div className="relative mb-3">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-500" />
          <input
            type="text"
            placeholder="Buscar por paciente, CUIR o DNI"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-[#E3E3E8] rounded-xl text-[16px] placeholder:text-gray-700 outline-none focus:bg-white focus:ring-2 focus:ring-[#0FA3B1]/40 transition-all"
          />
        </div>

        {/* Control segmentado, como los de iOS, en vez del desplegable */}
        <div className="flex gap-0.5 p-0.5 bg-[#E3E3E8] rounded-xl mb-5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {[
            { v: '', t: 'Todas' },
            { v: 'active', t: 'Activas' },
            { v: 'dispensed', t: 'Dispensadas' },
            { v: 'expired', t: 'Expiradas' },
            { v: 'cancelled', t: 'Canceladas' },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => setStatusFilter(o.v)}
              className={`flex-1 whitespace-nowrap px-3 py-1.5 rounded-[10px] text-[13px] font-medium transition-all ${
                statusFilter === o.v
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-700 active:opacity-60'
              }`}
            >
              {o.t}
            </button>
          ))}
        </div>

        {/* ---------------------------------------------------------------
            Lista agrupada. Se dejó de usar tabla: en el celular obligaba a
            desplazar de costado justo para ver el estado y las acciones,
            que es lo que uno mira. Ahora cada receta es una fila.
            --------------------------------------------------------------- */}
        {loading ? (
          <div className="py-16 text-center">
            <div className="animate-spin w-7 h-7 border-[3px] border-[#0FA3B1] border-t-transparent rounded-full mx-auto mb-3"></div>
            <p className="text-gray-700 text-[15px]">Cargando…</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl p-8 text-center">
            <XCircle className="w-10 h-10 text-[#FF3B30] mx-auto mb-3" />
            <p className="text-gray-700 text-[15px] mb-4">{error}</p>
            <button
              onClick={loadPrescriptions}
              className="bg-[#E3E3E8] text-gray-900 px-5 py-2.5 rounded-xl font-semibold text-[15px] active:opacity-60"
            >
              Reintentar
            </button>
          </div>
        ) : filteredPrescriptions.length === 0 ? (
          <div className="bg-white rounded-2xl py-14 px-8 text-center">
            <div className="w-16 h-16 bg-[#0FA3B1]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileSignature className="w-8 h-8 text-[#0B7C87]" />
            </div>
            <h3 className="text-[17px] font-semibold text-gray-900 mb-1">
              {searchTerm || statusFilter ? 'Sin resultados' : 'Todavía no emitiste recetas'}
            </h3>
            <p className="text-gray-700 text-[15px]">
              {searchTerm || statusFilter
                ? 'Probá con otro texto o cambiá el filtro'
                : 'Cuando emitas la primera, va a aparecer acá'}
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-2xl overflow-hidden">
              {filteredPrescriptions.map((prescription, i) => (
                <div
                  key={prescription.id}
                  onClick={() => abrirReceta(prescription)}
                  className="relative px-4 py-3.5 active:bg-gray-50 transition-colors cursor-pointer"
                >
                  {/* Separador con sangría, como las listas del sistema */}
                  {i > 0 && <div className="absolute top-0 left-4 right-0 h-px bg-gray-200" />}

                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 text-[16px] truncate">
                        {prescription.patient_name}
                      </p>
                      {prescription.patient_dni && (
                        <p className="text-[13px] text-gray-700">DNI {prescription.patient_dni}</p>
                      )}
                      {/* El número que sirve en la farmacia es el de Qbi2. El
                          CUIR nuestro solo se muestra si no hay otro. */}
                      <p className="font-mono text-[13px] text-[#0B7C87] mt-1 truncate">
                        {prescription.qbi2_cuir || prescription.qbi2_id_receta || prescription.cuir}
                      </p>
                      <p className="text-[12px] text-gray-500 mt-1">
                        Emitida {formatDate(prescription.issued_at)} · Vence {formatDate(prescription.expires_at)}
                      </p>
                    </div>

                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      {getStatusBadge(prescription.status)}

                      {/* stopPropagation: la fila entera abre el PDF, y sin
                          esto compartir haría las dos cosas a la vez. */}
                      {prescription.qbi2_s3_link && (
                        <button
                          onClick={(e) => { e.stopPropagation(); compartirReceta(prescription); }}
                          aria-label="Enviar la receta al paciente"
                          title="Enviar al paciente"
                          className="w-9 h-9 rounded-full bg-[#0FA3B1]/10 flex items-center justify-center active:scale-95 transition-transform"
                        >
                          <Share2 className="w-[18px] h-[18px] text-[#0B7C87]" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); window.open(`/receta/${prescription.cuir}`, '_blank'); }}
                        className="p-1.5 text-gray-500 active:text-[#0B7C87] transition-colors"
                        title="Ver QR"
                      >
                        <QrCode className="w-[18px] h-[18px]" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginación */}
            {pagination.pages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 bg-white rounded-xl text-[15px] font-medium text-[#0B7C87] disabled:opacity-40 active:opacity-60"
                >
                  Anterior
                </button>
                <p className="text-[13px] text-gray-700">
                  {pagination.page} de {pagination.pages}
                </p>
                <button
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 bg-white rounded-xl text-[15px] font-medium text-[#0B7C87] disabled:opacity-40 active:opacity-60"
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}

        {/* Modal Nueva Receta */}
        {showNewPrescription && (
          <PrescriptionWizard
            isOpen={showNewPrescription}
            pacienteInicial={pacienteDesdeTurno}
            onClose={() => {
              setShowNewPrescription(false);
              setPacienteDesdeTurno(null);
            }}
            onSuccess={() => {
              setShowNewPrescription(false);
              setPacienteDesdeTurno(null);
              loadPrescriptions();
            }}
          />
        )}

        {/* Ajustes: matrícula y logo de la receta */}
        <PrescriptionSettingsModal
          isOpen={showAjustes}
          onClose={() => setShowAjustes(false)}
          onCambios={() => {
            // Quitar la matrícula cambia si se puede emitir o no, así que se
            // vuelve a leer el estado en vez de dejar la pantalla desfasada.
            //
            // verificarHabilitacion() es la que importa acá y faltaba: sin
            // ella, quien borraba su matrícula desde este mismo modal seguía
            // viendo "Nueva receta", porque la habilitación se había
            // consultado al entrar a la pantalla y nadie la volvía a mirar.
            // No hay remontaje: el modal se abre y se cierra encima.
            verificarHabilitacion();
            check2FAStatus();
            loadPrescriptions();
          }}
        />
      </div>
    </div>
  );
};

// ===================================================================
// MODAL: Nueva Receta Electrónica
// ===================================================================
const NewPrescriptionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ isOpen, onClose, onSuccess }) => {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Datos del paciente (sex requerido por Decreto 63/2024)
  const [patient, setPatient] = useState({
    name: '',
    dni: '',
    sex: '' as '' | 'F' | 'M' | 'X',
    birthDate: '',
    healthInsurance: '',
    insuranceNumber: ''
  });
  
  // Diagnóstico
  const [diagnosis, setDiagnosis] = useState({
    code: '',
    description: ''
  });
  
  // Medicamentos (Decreto 63/2024 exige Forma farmacéutica separada)
  const [medications, setMedications] = useState<Array<{
    medication: string;
    commercialName: string;
    pharmaceuticalForm: string;
    presentation: string;
    quantity: number;
    dosage: string;
    duration: string;
    notes: string;
  }>>([{
    medication: '',
    commercialName: '',
    pharmaceuticalForm: '',
    presentation: '',
    quantity: 1,
    dosage: '',
    duration: '',
    notes: ''
  }]);
  
  // Firma manuscrita (PNG dataURL)
  const [signature, setSignature] = useState<string | null>(null);

  // Resultado
  const [result, setResult] = useState<{
    cuir: string;
    qrCode: string;
  } | null>(null);

  const resetForm = () => {
    setStep(1);
    setPatient({ name: '', dni: '', sex: '', birthDate: '', healthInsurance: '', insuranceNumber: '' });
    setDiagnosis({ code: '', description: '' });
    setMedications([{ medication: '', commercialName: '', pharmaceuticalForm: '', presentation: '', quantity: 1, dosage: '', duration: '', notes: '' }]);
    setSignature(null);
    setResult(null);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const addMedication = () => {
    setMedications([...medications, {
      medication: '',
      commercialName: '',
      pharmaceuticalForm: '',
      presentation: '',
      quantity: 1,
      dosage: '',
      duration: '',
      notes: ''
    }]);
  };

  const removeMedication = (index: number) => {
    if (medications.length > 1) {
      setMedications(medications.filter((_, i) => i !== index));
    }
  };

  const updateMedication = (index: number, field: string, value: string | number) => {
    const updated = [...medications];
    updated[index] = { ...updated[index], [field]: value };
    setMedications(updated);
  };

  const validateStep1 = () => {
    if (!patient.name.trim()) {
      setError('El nombre del paciente es requerido');
      return false;
    }
    if (!patient.dni.trim()) {
      setError('El DNI del paciente es requerido (Decreto 63/2024)');
      return false;
    }
    if (!patient.sex) {
      setError('El sexo del paciente es requerido (Decreto 63/2024)');
      return false;
    }
    if (!patient.birthDate) {
      setError('La fecha de nacimiento del paciente es requerida (Decreto 63/2024)');
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    const hasValidMedication = medications.some(m => m.medication.trim() !== '');
    if (!hasValidMedication) {
      setError('Debe agregar al menos un medicamento');
      return false;
    }
    // Cada medicamento con nombre debe tener forma farmacéutica (Decreto 63/2024)
    const missingForm = medications.find(
      m => m.medication.trim() !== '' && !m.pharmaceuticalForm
    );
    if (missingForm) {
      setError(`Falta seleccionar la forma farmacéutica de "${missingForm.medication}" (Decreto 63/2024)`);
      return false;
    }
    setError('');
    return true;
  };

  const validateStep3 = () => {
    if (!signature) {
      setError('Debés firmar la receta antes de emitirla');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/prescriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          patient,
          diagnosis,
          prescriptions: medications.filter(m => m.medication.trim() !== ''),
          signature
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error creando receta');
      }
      
      setResult({
        cuir: data.prescription.cuir,
        qrCode: data.prescription.qrCode
      });
      setStep(4);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = () => {
    onSuccess();
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Nueva Receta Electrónica</h2>
              <p className="text-sm text-gray-700">Paso {step} de 4</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XCircle className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="flex gap-2 mt-4">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  s <= step ? 'bg-purple-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Datos del paciente */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <User className="w-5 h-5 text-purple-500" />
                Datos del Paciente
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    value={patient.name}
                    onChange={(e) => setPatient({ ...patient, name: e.target.value })}
                    placeholder="Nombre y apellido del paciente"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    DNI *
                  </label>
                  <input
                    type="text"
                    value={patient.dni}
                    onChange={(e) => setPatient({ ...patient, dni: e.target.value })}
                    placeholder="12345678"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de nacimiento *
                  </label>
                  <input
                    type="date"
                    value={patient.birthDate}
                    onChange={(e) => setPatient({ ...patient, birthDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sexo *
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {([
                      { value: 'F', label: 'Femenino' },
                      { value: 'M', label: 'Masculino' },
                      { value: 'X', label: 'No binario' },
                    ] as const).map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer transition-colors ${
                          patient.sex === opt.value
                            ? 'border-purple-500 bg-purple-50 text-purple-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          name="patient-sex"
                          value={opt.value}
                          checked={patient.sex === opt.value}
                          onChange={() => setPatient({ ...patient, sex: opt.value })}
                          className="accent-purple-600"
                        />
                        <span className="text-sm">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OOSS/Plan Médico
                  </label>
                  <input
                    type="text"
                    value={patient.healthInsurance}
                    onChange={(e) => setPatient({ ...patient, healthInsurance: e.target.value })}
                    placeholder="Ej: OSDE, Swiss Medical, Particular"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan / Nº de afiliado
                  </label>
                  <input
                    type="text"
                    value={patient.insuranceNumber}
                    onChange={(e) => setPatient({ ...patient, insuranceNumber: e.target.value })}
                    placeholder="Plan o número de afiliado"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Medicamentos */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Pill className="w-5 h-5 text-purple-500" />
                  Medicamentos / Indicaciones
                </h3>
                <button
                  onClick={addMedication}
                  className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Agregar
                </button>
              </div>
              
              {/* Diagnóstico */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Diagnóstico (opcional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={diagnosis.code}
                    onChange={(e) => setDiagnosis({ ...diagnosis, code: e.target.value })}
                    placeholder="Código CIE-10"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    value={diagnosis.description}
                    onChange={(e) => setDiagnosis({ ...diagnosis, description: e.target.value })}
                    placeholder="Descripción del diagnóstico"
                    className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              {/* Lista de medicamentos */}
              <div className="space-y-4">
                {medications.map((med, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Medicamento {index + 1}
                      </span>
                      {medications.length > 1 && (
                        <button
                          onClick={() => removeMedication(index)}
                          className="text-red-500 hover:text-red-600 p-1"
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={med.medication}
                        onChange={(e) => updateMedication(index, 'medication', e.target.value)}
                        placeholder="Nombre genérico / IFA *"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={med.commercialName}
                        onChange={(e) => updateMedication(index, 'commercialName', e.target.value)}
                        placeholder="Nombre comercial (opcional)"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <select
                        value={med.pharmaceuticalForm}
                        onChange={(e) => updateMedication(index, 'pharmaceuticalForm', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                      >
                        <option value="">Forma farmacéutica *</option>
                        <option value="Comprimidos">Comprimidos</option>
                        <option value="Cápsulas">Cápsulas</option>
                        <option value="Comprimidos recubiertos">Comprimidos recubiertos</option>
                        <option value="Comprimidos sublinguales">Comprimidos sublinguales</option>
                        <option value="Jarabe">Jarabe</option>
                        <option value="Suspensión">Suspensión</option>
                        <option value="Solución oral">Solución oral</option>
                        <option value="Gotas">Gotas</option>
                        <option value="Ampollas">Ampollas</option>
                        <option value="Inyectable">Inyectable</option>
                        <option value="Crema">Crema</option>
                        <option value="Pomada">Pomada</option>
                        <option value="Gel">Gel</option>
                        <option value="Loción">Loción</option>
                        <option value="Spray">Spray</option>
                        <option value="Aerosol">Aerosol</option>
                        <option value="Supositorios">Supositorios</option>
                        <option value="Óvulos">Óvulos</option>
                        <option value="Parches">Parches</option>
                        <option value="Polvo">Polvo</option>
                        <option value="Otra">Otra</option>
                      </select>
                      <input
                        type="text"
                        value={med.presentation}
                        onChange={(e) => updateMedication(index, 'presentation', e.target.value)}
                        placeholder="Presentación (ej: 500mg x 20 unid.)"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <input
                        type="number"
                        value={med.quantity}
                        onChange={(e) => updateMedication(index, 'quantity', parseInt(e.target.value) || 1)}
                        placeholder="Cantidad"
                        min="1"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={med.dosage}
                        onChange={(e) => updateMedication(index, 'dosage', e.target.value)}
                        placeholder="Posología (ej: 1 cada 8hs)"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={med.duration}
                        onChange={(e) => updateMedication(index, 'duration', e.target.value)}
                        placeholder="Duración (ej: 7 días)"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={med.notes}
                        onChange={(e) => updateMedication(index, 'notes', e.target.value)}
                        placeholder="Notas adicionales"
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent md:col-span-2"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Confirmación y firma */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-purple-500" />
                Confirmar y Firmar
              </h3>
              
              {/* Resumen */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <h4 className="font-medium text-gray-900">Resumen de la receta</h4>
                
                <div className="text-sm space-y-2">
                  <p><span className="text-gray-700">Paciente:</span> {patient.name}</p>
                  {patient.dni && <p><span className="text-gray-700">DNI:</span> {patient.dni}</p>}
                  {diagnosis.description && (
                    <p><span className="text-gray-700">Diagnóstico:</span> {diagnosis.description}</p>
                  )}
                </div>
                
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Medicamentos:</p>
                  <ul className="text-sm space-y-1">
                    {medications.filter(m => m.medication).map((med, i) => (
                      <li key={i} className="text-gray-700">
                        • {med.medication}{med.commercialName && ` (${med.commercialName})`} {med.presentation && `- ${med.presentation}`} - {med.quantity} unidad(es)
                        {med.dosage && ` - ${med.dosage}`}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Firma manuscrita */}
              <div className="p-4 border-2 border-purple-200 rounded-lg bg-purple-50">
                <SignaturePad
                  onChange={(dataUrl) => setSignature(dataUrl)}
                  errorMessage={
                    error && error.toLowerCase().includes('firma') ? error : undefined
                  }
                />
              </div>
            </div>
          )}

          {/* Step 4: Éxito */}
          {step === 4 && result && (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-gray-900">¡Receta emitida exitosamente!</h3>
                <p className="text-gray-700 mt-1">La receta ha sido firmada y registrada</p>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700 mb-1">Código CUIR</p>
                <p className="font-mono text-lg font-bold text-purple-600">{result.cuir}</p>
              </div>
              
              {result.qrCode && (
                <div className="flex flex-col items-center">
                  <p className="text-sm text-gray-700 mb-2">Código QR de validación</p>
                  <img src={result.qrCode} alt="QR Code" className="w-48 h-48" />
                </div>
              )}
              
              <p className="text-sm text-gray-700">
                El paciente puede validar esta receta en cualquier farmacia usando el código CUIR o escaneando el QR.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl">
          <div className="flex justify-between">
            {step < 4 ? (
              <>
                <button
                  onClick={step === 1 ? handleClose : () => setStep(step - 1)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  {step === 1 ? 'Cancelar' : 'Anterior'}
                </button>
                
                {step < 3 ? (
                  <button
                    onClick={() => {
                      if (step === 1 && validateStep1()) setStep(2);
                      else if (step === 2 && validateStep2()) setStep(3);
                    }}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Siguiente
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Firmando...
                      </>
                    ) : (
                      <>
                        <FileSignature className="w-4 h-4" />
                        Firmar y Emitir
                      </>
                    )}
                  </button>
                )}
              </>
            ) : (
              <button
                onClick={handleFinish}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all font-semibold"
              >
                Finalizar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionManager;